/* =========================================================================
   ClueVine — session factory & finalizer
   Pure glue between a mode and the shell. createPuzzle() assembles the seeded
   GenerateContext and asks the mode to build a puzzle; finalizeScore() asks the
   mode for its raw score and layers on the shared speed/difficulty/streak/hint
   adjustments. Timing and React state live in GameContext; this stays pure and
   UI-agnostic so it's trivially testable.
   ========================================================================= */

import { dictionary as defaultDictionary } from './dictionary';
import { computeScore } from './scoring';
import { seedPrng, type SeedInfo } from './seed';
import type {
  AnyPuzzleMode,
  DifficultyLevel,
  Dictionary,
  GenerateContext,
  ScoreBreakdown,
} from './types';

export interface CreatePuzzleArgs {
  mode: AnyPuzzleMode;
  seed: SeedInfo;
  difficulty: DifficultyLevel;
  avoid?: ReadonlySet<string>;
  dictionary?: Dictionary;
}

const NO_AVOID: ReadonlySet<string> = new Set<string>();

/**
 * Deterministically build a fresh puzzle state for a mode. Daily puzzles ignore
 * `avoid`: filtering the candidate pool by a player's own history would shift
 * the seeded pick and hand every returning player a different "shared" daily.
 */
export function createPuzzle(args: CreatePuzzleArgs): unknown {
  const { mode, seed, difficulty, dictionary = defaultDictionary } = args;
  const avoid = seed.playMode === 'daily' ? NO_AVOID : (args.avoid ?? NO_AVOID);
  const ctx: GenerateContext = {
    prng: seedPrng(seed),
    difficulty,
    dictionary,
    playMode: seed.playMode,
    avoid,
  };
  return mode.generate(ctx);
}

export interface FinalizeArgs {
  mode: AnyPuzzleMode;
  state: unknown;
  elapsedMs: number;
  difficulty: DifficultyLevel;
  streak: number;
  hintsUsed: number;
  hintPenalty: number;
}

/** Combine a mode's raw score with the shared scoring layer. */
export function finalizeScore(args: FinalizeArgs): ScoreBreakdown {
  const modeScore = args.mode.score(args.state);
  return computeScore(modeScore, {
    elapsedMs: args.elapsedMs,
    difficulty: args.difficulty,
    streak: args.streak,
    hintsUsed: args.hintsUsed,
    hintPenalty: args.hintPenalty,
  });
}
